import React from "react";
import { Download, BookOpen, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLeadMagnet } from "@/components/landing/LeadMagnet";

/**
 * Compact lead-magnet card for blog/sidebar contexts.
 * Reuses the form logic from <LeadMagnet />.
 */
export default function LeadMagnetCard({ context = "blog" }) {
  const { t, state, onSubmit, pdfUrl } = useLeadMagnet();
  return (
    <aside
      data-testid="lead-magnet-card"
      className="bg-[#1e2a5a] text-[#f4f1ea] rounded-2xl p-6 sm:p-8 my-12 sm:my-16 not-prose"
    >
      {state.success ? (
        <div className="text-center py-2">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#ff8a7a]/20 text-[#ff8a7a] mb-4">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h3 className="font-display text-xl sm:text-2xl tracking-tight">
            {t.successTitle}
          </h3>
          <p className="mt-2 text-sm text-[#d4d2cb]">{t.successBody}</p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="lead-magnet-card-download"
            className="group mt-5 inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] px-5 py-3 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#ff8a7a] hover:text-[#1e2a5a]"
          >
            <Download className="h-4 w-4" /> {t.successCta}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#ff8a7a] mb-3">
            <BookOpen className="h-3.5 w-3.5" /> {t.overline}
          </div>
          <h3 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">
            {t.title}
          </h3>
          <p className="mt-3 text-sm text-[#d4d2cb] leading-relaxed">
            {t.subtitle.split(". ")[0]}.
          </p>
          <form
            onSubmit={(e) => onSubmit(e, context)}
            className="mt-6 grid sm:grid-cols-[1fr_1fr_auto] gap-2"
          >
            <Input
              data-testid="lead-magnet-card-name"
              type="text"
              required
              placeholder={t.nameLabel}
              autoComplete="given-name"
              value={state.name}
              onChange={(e) => state.setName(e.target.value)}
              className="bg-white text-[#1a1a1a] border-transparent focus-visible:ring-[#ff8a7a]/40 h-11"
            />
            <Input
              data-testid="lead-magnet-card-email"
              type="email"
              required
              placeholder={t.emailLabel}
              autoComplete="email"
              value={state.email}
              onChange={(e) => state.setEmail(e.target.value)}
              className="bg-white text-[#1a1a1a] border-transparent focus-visible:ring-[#ff8a7a]/40 h-11"
            />
            <button
              type="submit"
              data-testid="lead-magnet-card-submit"
              disabled={state.submitting}
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-[#d9381e] px-5 py-3 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#ff8a7a] hover:text-[#1e2a5a] disabled:opacity-60 disabled:cursor-not-allowed h-11 whitespace-nowrap"
            >
              {state.submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">{t.cta}</span>
                  <span className="sm:hidden">PDF</span>
                </>
              )}
            </button>
          </form>
          {/* Honeypot */}
          <input
            type="text"
            tabIndex="-1"
            autoComplete="off"
            value={state.website}
            onChange={(e) => state.setWebsite(e.target.value)}
            className="hidden"
            aria-hidden="true"
          />
          {state.error && (
            <p className="mt-3 text-sm text-[#ff8a7a]">{state.error}</p>
          )}
          <p className="mt-3 text-[11px] text-[#9a9aa6] leading-relaxed">
            {t.consent}
          </p>
        </>
      )}
    </aside>
  );
}
